/**
 * 关联人脸库弹窗
 */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Card, Image, Row, Col, Spin, message, Form, Select, Button, Radio, Empty, Space, Descriptions } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { getDriverFaceInfo, aliyunFaceDetect, syncFaceInfoNew, syncFaceInfoUpdate } from '@/service/driverArchive';
import type { FaceInfo } from '@/service/driverArchive/define';
import styles from './index.module.scss';

const { Option } = Select;

interface LinkFaceLibModalProps {
    visible: boolean;
    onCancel: () => void;
    onOk: () => void;
    record: any;
    fleetOptions: any[]; // 车队选项
    fetchFleetList?: (name?: string) => void; // 车队搜索函数
}

interface PhotoData {
    driverImageFileUrl: any[]; // 司机照片
    driveLicenseFileUrl: any[]; // 驾驶证照片（正反面）
    idCardFileUrl: any[]; // 身份证照片（正反面）
}

interface FaceLibPhoto {
    id: string;
    url: string;
    name?: string;
}

const LinkFaceLibModal: React.FC<LinkFaceLibModalProps> = ({
    visible,
    onCancel,
    onOk,
    record,
    fleetOptions,
    fetchFleetList,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [photoData, setPhotoData] = useState<PhotoData>({
        driverImageFileUrl: [],
        driveLicenseFileUrl: [],
        idCardFileUrl: [],
    });
    const [faceLibPhotos, setFaceLibPhotos] = useState<FaceLibPhoto[]>([]); // 人脸库照片列表
    const [selectedPhotoId, setSelectedPhotoId] = useState<string>(''); // 选中的照片ID
    const [queryLoading, setQueryLoading] = useState(false); // 查询加载状态
    const [hasQueried, setHasQueried] = useState(false); // 是否已查询过
    
    // 本地上传相关状态
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('');
    const [faceDetectResult, setFaceDetectResult] = useState<FaceInfo | null>(null);

    // 获取照片信息
    const fetchPhotoData = async () => {
        if (!record?.id) return;

        setLoading(true);
        try {
            // 使用record中的数据
            setPhotoData({
                driverImageFileUrl: record?.driverImageFileUrl || [],
                driveLicenseFileUrl: record?.driveLicenseFileUrl || [],
                idCardFileUrl: record?.idCardFileUrl || [],
            });
        } catch (error) {
            message.error('获取照片信息失败');
            console.error('获取照片信息失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 组件加载时获取照片信息
    useEffect(() => {
        if (visible) {
            fetchPhotoData();
            // 重置状态
            setFaceLibPhotos([]);
            setSelectedPhotoId('');
            setHasQueried(false);
            setUploadedImageUrl('');
            setFaceDetectResult(null);
            form.resetFields();
        }

        // 清理函数：释放对象URL
        return () => {
            if (uploadedImageUrl) {
                URL.revokeObjectURL(uploadedImageUrl);
            }
        };
    }, [visible, record?.id]);

    // 查询人脸库
    const handleQuery = async () => {
        try {
            const values = await form.validateFields(['photoTakeCompany']);

            if (!values.photoTakeCompany) {
                message.warning('请先选择车队');
                return;
            }

            setQueryLoading(true);
            setSelectedPhotoId(''); // 清空之前的选择

            try {
                // 调用API接口查询人脸库
                const response = await getDriverFaceInfo({
                    photoTakeCompany: values.photoTakeCompany,
                    archiveFlag:0,
                    limit: 9999,
                    page: 1
                });

                const faceData = response.data?.data || [];
                // 将接口返回的数据转换为组件需要的格式
                const photos = faceData.map((item: any) => ({
                    id: String(item.id),
                    url: item.faceUrl,
                    name: item.driverName || `照片 ${item.id}`,
                }));

                setFaceLibPhotos(photos);
                setHasQueried(true);
                message.success(`查询成功，共找到 ${photos.length} 条记录`);
            } catch (error) {
                message.error('查询人脸库失败');
                console.error('查询人脸库失败:', error);
                setFaceLibPhotos([]);
                setHasQueried(true);
            } finally {
                setQueryLoading(false);
            }
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    // 绑定人脸库
    const handleBindFaceLib = async () => {
        if (!selectedPhotoId) {
            message.warning('请先选择要关联的照片');
            return;
        }

        if (!record?.driverId) {
            message.error('缺少司机ID信息');
            return;
        }

        try {
            setLoading(true);

            // 从查询结果中找到选中的照片完整数据
            const response = await getDriverFaceInfo({
                companyId: form.getFieldValue('fleetId'),
                limit: 9999,
                page: 1
            });

            const faceData = response.data?.data || [];
            const selectedFaceInfo = faceData.find((item: any) => String(item.id) === selectedPhotoId);

            if (!selectedFaceInfo) {
                message.error('未找到选中的照片信息');
                return;
            }

            // 构造传参数据，使用选中照片的数据，但替换 driverId
            const requestData: FaceInfo = {
                beard: selectedFaceInfo.beard,
                beardConfidence: selectedFaceInfo.beardConfidence,
                driverId: record.driverId, // 使用从表格行传递过来的 driverId
                driverRegisterRecordId: record.id,
                faceQuality: selectedFaceInfo.faceQuality,
                figureConfidence: selectedFaceInfo.figureConfidence,
                figureType: selectedFaceInfo.figureType,
                gender: selectedFaceInfo.gender,
                genderConfidence: selectedFaceInfo.genderConfidence,
                glasses: selectedFaceInfo.glasses,
                glassesConfidence: selectedFaceInfo.glassesConfidence,
                hat: selectedFaceInfo.hat,
                hatConfidence: selectedFaceInfo.hatConfidence,
                mask: selectedFaceInfo.mask,
                maskConfidence: selectedFaceInfo.maskConfidence,
                mouth: selectedFaceInfo.mouth,
                mouthConfidence: selectedFaceInfo.mouthConfidence,
                ossBucket: selectedFaceInfo.ossBucket,
                ossKey: selectedFaceInfo.ossKey,
                sharpness: selectedFaceInfo.sharpness,
            };

            // 调用同步人脸信息接口（更新）
            const updateResponse = await syncFaceInfoUpdate(selectedPhotoId, requestData);

            if (updateResponse.data?.success) {
                message.success('关联成功！');
                onOk(); // 关闭弹窗并刷新列表
            } else {
                message.error(updateResponse.data?.msg || '关联失败');
            }
        } catch (error) {
            message.error('关联失败，请重试');
            console.error('关联失败:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOk = () => {
        handleBindFaceLib();
    };

    // 选择照片
    const handlePhotoSelect = (photoId: string) => {
        setSelectedPhotoId(photoId);
    };

    // 使用本地上传的图片进行绑定
    const handleBindLocalUpload = async () => {
        if (!faceDetectResult) {
            message.warning('请先上传图片并完成人脸检测');
            return;
        }

        if (!record?.driverId) {
            message.error('缺少司机ID信息');
            return;
        }

        try {
            setLoading(true);
            
            // 构造传参数据，使用人脸检测结果，但替换 driverId
            const requestData: FaceInfo = {
                ...faceDetectResult,
                driverId: record.driverId, // 使用从表格行传递过来的 driverId
                driverRegisterRecordId: record.id,
            };

            // 调用同步人脸信息接口
            const response = await syncFaceInfoNew(requestData);

            if (response.data?.success) {
                message.success('绑定成功！');
                onOk(); // 关闭弹窗并刷新列表
            } else {
                message.error(response.data?.msg || '绑定失败');
            }
        } catch (error) {
            message.error('绑定失败，请重试');
            console.error('绑定失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 触发文件选择
    const handleLocalUploadClick = () => {
        fileInputRef.current?.click();
    };

    // 处理文件选择
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // 验证文件类型
        if (!file.type.startsWith('image/')) {
            message.error('只能上传图片文件！');
            return;
        }

        // 验证文件大小（最大5MB）
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            message.error('图片大小不能超过 5MB！');
            return;
        }

        // 开始上传
        setUploadLoading(true);

        try {
            // 创建FormData
            const formData = new FormData();
            formData.append('file', file);

            // 调用阿里云人脸检测接口
            const response = await aliyunFaceDetect(formData);

            if (response.data?.success && response.data?.data) {
                const faceInfo = response.data.data;
                setFaceDetectResult(faceInfo);

                // 清除之前的URL
                if (uploadedImageUrl) {
                    URL.revokeObjectURL(uploadedImageUrl);
                }

                // 生成新的预览URL
                const imageUrl = URL.createObjectURL(file);
                setUploadedImageUrl(imageUrl);

                message.success('人脸检测成功！');
            } else {
                message.error(response.data?.msg || '人脸检测失败');
                setFaceDetectResult(null);
                setUploadedImageUrl('');
            }
        } catch (error) {
            message.error('上传失败，请重试');
            console.error('上传失败:', error);
            setFaceDetectResult(null);
            setUploadedImageUrl('');
        } finally {
            setUploadLoading(false);
            // 清空input的值，允许重复选择同一个文件
            if (event.target) {
                event.target.value = '';
            }
        }
    };

    // 渲染照片卡片 - 支持数组格式
    const renderPhotoCard = (title: string, imageArray: any[]) => {
        return (
            <Col span={8}>
                <Card title={title} className={styles.photoCard} size="small">
                    <div className={styles.photoWrapper}>
                        {imageArray && imageArray.length > 0 ? (
                            <Image.PreviewGroup>
                                <Space>
                                    {imageArray.map((item, index) => (
                                        <Image
                                            key={index}
                                            src={item.ossUrl || ''}
                                            alt={`${title}-${index + 1}`}
                                            width={120}
                                            height={120}
                                            style={{ objectFit: 'cover' }}
                                            placeholder={
                                                <div className={styles.placeholder}>加载中...</div>
                                            }
                                        />
                                    ))}
                                </Space>
                            </Image.PreviewGroup>
                        ) : (
                            <div className={styles.noPhoto}>暂无照片</div>
                        )}
                    </div>
                </Card>
            </Col>
        );
    };

    return (
        <Modal
            title="关联人脸库"
            open={visible}
            onCancel={onCancel}
            onOk={handleOk}
            okText="确定关联"
            cancelText="取消"
            okButtonProps={{ disabled: !selectedPhotoId }}
            width="80%"
            maskClosable={false}
            className={styles.linkFaceLibModal}
        >
            <Spin spinning={loading}>
                <div className={styles.modalContent}>
                    {/* 第一部分：照片展示 - 按照司机照片、驾驶证照片、身份证照片的顺序 */}
                    <Row gutter={[16, 16]}>
                        {renderPhotoCard('司机照片', photoData.driverImageFileUrl)}
                        {renderPhotoCard('驾驶证照片', photoData.driveLicenseFileUrl)}
                        {renderPhotoCard('身份证照片', photoData.idCardFileUrl)}
                    </Row>

                    {/* 第二部分：车队选择和查询 */}
                    <Card title="查询人脸库" className={styles.sectionCard} style={{ marginTop: 16 }}>
                        <Form form={form} layout="inline">
                            <Form.Item
                                name="photoTakeCompany"
                                label="照片拍摄所在车队"
                                rules={[{ required: true, message: '请选择车队' }]}
                            >
                                <Select
                                    placeholder="请选择车队"
                                    style={{ width: 200 }}
                                    allowClear
                                    showSearch
                                    onSearch={(value) => fetchFleetList?.(value)}
                                    filterOption={false}
                                >
                                    {fleetOptions.map((fleet) => (
                                        <Option key={fleet.label} value={fleet.label}>
                                            {fleet.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" onClick={handleQuery} loading={queryLoading}>
                                    查询
                                </Button>

                            </Form.Item>
                            {/* 隐藏的文件input */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <Button 
                                icon={<UploadOutlined />}
                                onClick={handleLocalUploadClick}
                                loading={uploadLoading}
                            >
                                没有找到合适的照片，使用本地上传
                            </Button>
                        </Form>
                    </Card>

                    {/* 第三部分：查询结果展示 */}
                    {hasQueried && (
                        <Card title="查询结果" className={styles.sectionCard} style={{ marginTop: 16 }}>
                            <Spin spinning={queryLoading}>
                                {faceLibPhotos.length > 0 ? (
                                    <Radio.Group
                                        value={selectedPhotoId}
                                        onChange={(e) => handlePhotoSelect(e.target.value)}
                                        className={styles.photoRadioGroup}
                                    >
                                        <Row gutter={[16, 16]}>
                                            {faceLibPhotos.map((photo) => (
                                                <Col span={4} key={photo.id}>
                                                    <div
                                                        className={`${styles.facePhotoItem} ${selectedPhotoId === photo.id ? styles.selected : ''
                                                            }`}
                                                        onClick={() => handlePhotoSelect(photo.id)}
                                                    >
                                                        <div className={styles.photoContainer}>
                                                            <Image
                                                                src={photo.url}
                                                                alt={photo.name || '人脸照片'}
                                                                className={styles.facePhoto}
                                                                preview={true}
                                                            />
                                                        </div>
                                                        <div className={styles.radioWrapper}>
                                                            <Radio value={photo.id}>
                                                                {photo.name || `照片 ${photo.id}`}
                                                            </Radio>
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Radio.Group>
                                ) : (
                                    <Empty description="暂无查询结果" />
                                )}
                            </Spin>
                        </Card>
                    )}

                    {/* 第四部分：本地上传结果展示 */}
                    {uploadedImageUrl && faceDetectResult && (
                        <Card 
                            title="人脸检测结果" 
                            className={styles.sectionCard} 
                            style={{ marginTop: 16 }}
                            extra={
                                <Button 
                                    type="primary" 
                                    onClick={handleBindLocalUpload}
                                >
                                    使用当前图片
                                </Button>
                            }
                        >
                            <Row gutter={16}>
                                <Col span={8}>
                                    <div style={{ textAlign: 'center' }}>
                                        <Image
                                            src={uploadedImageUrl}
                                            alt="上传的照片"
                                            style={{ maxWidth: '100%', maxHeight: 300 }}
                                        />
                                    </div>
                                </Col>
                                <Col span={16}>
                                    <Descriptions column={2} bordered size="small">
                                        <Descriptions.Item label="性别">
                                            {faceDetectResult.gender || '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="性别置信度">
                                            {faceDetectResult.genderConfidence 
                                                ? `${(faceDetectResult.genderConfidence * 100).toFixed(2)}%` 
                                                : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="人脸质量">
                                            {faceDetectResult.faceQuality 
                                                ? faceDetectResult.faceQuality.toFixed(2) 
                                                : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="清晰度">
                                            {faceDetectResult.sharpness 
                                                ? faceDetectResult.sharpness.toFixed(2) 
                                                : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="是否戴眼镜">
                                            {faceDetectResult.glasses || '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="眼镜置信度">
                                            {faceDetectResult.glassesConfidence 
                                                ? `${(faceDetectResult.glassesConfidence * 100).toFixed(2)}%` 
                                                : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="是否戴口罩">
                                            {faceDetectResult.mask || '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="口罩置信度">
                                            {faceDetectResult.maskConfidence 
                                                ? `${(faceDetectResult.maskConfidence * 100).toFixed(2)}%` 
                                                : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="是否戴帽子">
                                            {faceDetectResult.hat || '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="帽子置信度">
                                            {faceDetectResult.hatConfidence 
                                                ? `${(faceDetectResult.hatConfidence * 100).toFixed(2)}%` 
                                                : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="是否有胡子">
                                            {faceDetectResult.beard || '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="胡子置信度">
                                            {faceDetectResult.beardConfidence 
                                                ? `${(faceDetectResult.beardConfidence * 100).toFixed(2)}%` 
                                                : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="是否张口">
                                            {faceDetectResult.mouth || '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="张口置信度">
                                            {faceDetectResult.mouthConfidence 
                                                ? `${(faceDetectResult.mouthConfidence * 100).toFixed(2)}%` 
                                                : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="人物类型">
                                            {faceDetectResult.figureType || '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="人物置信度">
                                            {faceDetectResult.figureConfidence 
                                                ? `${(faceDetectResult.figureConfidence * 100).toFixed(2)}%` 
                                                : '-'}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Col>
                            </Row>
                        </Card>
                    )}
                </div>
            </Spin>
        </Modal>
    );
};

export default LinkFaceLibModal;
